import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Product, ProductBody } from '../domain/product';
import { productsApi } from '../infrastructure/api';

export const productKeys = {
  all: ['products'] as const,
  list: () => [...productKeys.all, 'list'] as const,
  one: (id: string) => [...productKeys.all, 'one', id] as const,
};

export const productsQuery = queryOptions({ queryKey: productKeys.list(), queryFn: productsApi.list });
export const productQuery = (id: string) =>
  queryOptions({ queryKey: productKeys.one(id), queryFn: () => productsApi.get(id) });

export const useProducts = () => useQuery(productsQuery);
export const useProduct = (id: string) => useQuery(productQuery(id));

function useProductMutation<V>(fn: (vars: V) => Promise<Product>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (p) => {
      qc.setQueryData(productKeys.one(p.id), p);
      void qc.invalidateQueries({ queryKey: productKeys.list() });
    },
  });
}

export const useSaveProduct = () =>
  useProductMutation(({ id, body }: { id?: string; body: ProductBody }) =>
    id ? productsApi.update(id, body) : productsApi.create(body),
  );

export const useProductStatus = () =>
  useProductMutation(({ id, action }: { id: string; action: 'publish' | 'unpublish' | 'archive' }) =>
    productsApi[action](id),
  );

export function useDeleteProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => productsApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: productKeys.all }),
  });
}
